import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import Image from 'next/image';
import { usePicoXTrack } from '@eventbrite/bsp-sdk/react';
import { useIsCreatorAcquisitionPromptOnHomepageEnabled } from '@/bsp/config';
import {
    buildCreatorPromptCtaUrl,
    CREATOR_ACQUISITION_PROMPT_EVENT_CLICKED,
    CREATOR_ACQUISITION_PROMPT_EVENT_RENDERED,
    CREATOR_ACQUISITION_PROMPT_EXPERIMENT_NAME,
    CREATOR_ACQUISITION_PROMPT_PLACEMENT,
    CREATOR_ACQUISITION_PROMPT_SURFACE,
    CREATOR_ACQUISITION_PROMPT_VARIANT,
    getCreatorPromptDevice,
    type CreatorPromptDevice,
} from './constants';
import creatorRunner from './assets/creator-runner.png';
import styles from './CreatorAcquisitionPrompt.module.scss';

export interface CreatorAcquisitionPromptProps {
    className?: string;
}

export const CreatorAcquisitionPrompt: React.FC<CreatorAcquisitionPromptProps> = ({
    className,
}) => {
    const { isEnabled } = useIsCreatorAcquisitionPromptOnHomepageEnabled();
    const trackPicoX = usePicoXTrack();
    const intl = useIntl();
    const promptRef = useRef<HTMLElement>(null);
    const hasTrackedRender = useRef(false);

    const [device, setDevice] = useState<CreatorPromptDevice>(() => {
        if (typeof window !== 'undefined') {
            return getCreatorPromptDevice(window.innerWidth);
        }
        return 'desktop';
    });

    useEffect(() => {
        const handleResize = () => {
            setDevice(getCreatorPromptDevice(window.innerWidth));
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const trackEvent = useCallback(
        (eventName: string, currentDevice: CreatorPromptDevice) => {
            try {
                trackPicoX(eventName, {
                    surface: CREATOR_ACQUISITION_PROMPT_SURFACE,
                    placement: CREATOR_ACQUISITION_PROMPT_PLACEMENT,
                    device: currentDevice,
                    experiment_name: CREATOR_ACQUISITION_PROMPT_EXPERIMENT_NAME,
                    variant: CREATOR_ACQUISITION_PROMPT_VARIANT,
                });
            } catch {
                return;
            }
        },
        [trackPicoX],
    );

    useEffect(() => {
        if (
            !isEnabled ||
            hasTrackedRender.current ||
            !promptRef.current ||
            typeof IntersectionObserver === 'undefined'
        ) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (
                    !entries.some(
                        (entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5,
                    ) ||
                    hasTrackedRender.current
                ) {
                    return;
                }
                hasTrackedRender.current = true;
                const exposedDevice =
                    typeof window !== 'undefined'
                        ? getCreatorPromptDevice(window.innerWidth)
                        : device;
                trackEvent(CREATOR_ACQUISITION_PROMPT_EVENT_RENDERED, exposedDevice);
                observer.disconnect();
            },
            { threshold: 0.5 },
        );

        observer.observe(promptRef.current);
        return () => observer.disconnect();
    }, [isEnabled, trackEvent, device]);

    const handleCtaClick = useCallback(() => {
        trackEvent(CREATOR_ACQUISITION_PROMPT_EVENT_CLICKED, device);
    }, [trackEvent, device]);

    if (!isEnabled) {
        return null;
    }

    const ctaUrl = buildCreatorPromptCtaUrl(device);

    return (
        <aside
            ref={promptRef}
            className={`${styles.root}${className ? ` ${className}` : ''}`}
            aria-label={intl.formatMessage({
                id: 'creator_acquisition_prompt.aria_label',
                defaultMessage: 'Host on Eventbrite',
            })}
            data-testid="creator-acquisition-prompt"
        >
            <div className={styles.card}>
                <div className={styles.message}>
                    <div className={styles.icon} aria-hidden="true">
                        <Image
                            src={creatorRunner}
                            alt=""
                            width={88}
                            height={88}
                            unoptimized
                            className={styles.illustration}
                        />
                    </div>
                    <div className={styles.copy}>
                        <p className={styles.eyebrow}>
                            {intl.formatMessage({
                                id: 'creator_acquisition_prompt.eyebrow',
                                defaultMessage: 'Host on Eventbrite',
                            })}
                        </p>
                        <h2 className={styles.heading}>
                            {intl.formatMessage({
                                id: 'creator_acquisition_prompt.heading',
                                defaultMessage: 'Have an event of your own?',
                            })}
                        </h2>
                        <p className={styles.body}>
                            {intl.formatMessage({
                                id: 'creator_acquisition_prompt.body',
                                defaultMessage:
                                    'Sell tickets and get discovered by people looking for their next experience.',
                            })}
                        </p>
                    </div>
                </div>
                <div className={styles.action}>
                    <a
                        href={ctaUrl}
                        className={styles.cta}
                        onClick={handleCtaClick}
                        data-testid="creator-acquisition-prompt-cta"
                    >
                        {intl.formatMessage({
                            id: 'creator_acquisition_prompt.cta',
                            defaultMessage: 'Create an event',
                        })}
                    </a>
                </div>
            </div>
        </aside>
    );
};
